import { getAppConfigValue } from "@elliemae/pui-app-sdk";
import { v4 as uuidv4 } from "uuid";
import { formatDate, isEmpty, partitionArray, safeSum } from "@frontend/utils";
const AdjustmentType = {
  CustomRateAdjustment: "CustomRateAdjustment",
  LockExtensionAdjustment: "LockExtensionAdjustment",
  ReLockFeeAdjustment: "ReLockFeeAdjustment",
  CustomPriceAdjustment: "CustomPriceAdjustment",
  BranchPriceConcession: "BranchPriceConcession",
  CorporatePriceConcession: "CorporatePriceConcession"
};
const generateSectionTitle = (description, editable = false, handlerName) => ({
  id: uuidv4(),
  description,
  editable,
  type: "groupTitle",
  handlerName
});
const formatPriceValue = (p) => {
  if (p === null || p === void 0 || Number.isNaN(p)) return "";
  if (p === false) return "0.000";
  return Number(Number(p)?.toFixed(3));
};
const formatPriceValuePercent = (p) => {
  if (typeof p === "string" && p.endsWith("%")) return p;
  if (p === null || p === void 0 || Number.isNaN(p)) return "";
  if (p === "") return "-";
  return `${Number(p)?.toFixed(3)}%`;
};
const parseAdjustment = (adjustment) => {
  return {
    ...adjustment,
    rate: formatPriceValuePercent(adjustment?.rate),
    price: formatPriceValue(adjustment?.price),
    currentRate: formatPriceValuePercent(adjustment?.currentRate),
    currentPrice: formatPriceValue(adjustment?.currentPrice),
    change: formatPriceValue(adjustment?.change),
    type: "regular",
    id: adjustment.id || uuidv4()
  };
};
const formatAdjustmentsOrConcessions = ({
  adjustments,
  isValidate,
  rowsBeforeEdit,
  isConcession = false,
  pricingHasChanged
}) => adjustments.map((adj) => {
  if ((adj?.adjustmentType === AdjustmentType.ReLockFeeAdjustment || adj?.adjustmentType === AdjustmentType.CustomPriceAdjustment || adj?.adjustmentType === AdjustmentType.CustomRateAdjustment || adj?.adjustmentType === AdjustmentType.CorporatePriceConcession || adj?.adjustmentType === AdjustmentType.BranchPriceConcession) && adj?.description && adj?.description.includes("-")) {
    adj.description = adj?.description?.split("-")?.pop() || adj.description;
  }
  const newAdj = {
    ...adj,
    id: adj.id || uuidv4(),
    type: "regular",
    currentPrice: formatPriceValue(adj?.currentPrice),
    price: formatPriceValue(adj?.price),
    currentRate: formatPriceValuePercent(adj?.currentRate),
    rate: formatPriceValuePercent(adj?.rate),
    approvalDate: formatDate(adj?.approvalDate, "Calendar")
  };
  if (pricingHasChanged && isValidate && !isEmpty(rowsBeforeEdit) && newAdj.adjustmentType) {
    const oldPrice = rowsBeforeEdit.find(
      (oldAdj) => oldAdj.id === newAdj.id || oldAdj.adjustmentType === newAdj.adjustmentType && oldAdj.description === newAdj.description
    )?.price;
    newAdj.price = formatPriceValue(oldPrice) || "-";
    const oldRate = rowsBeforeEdit.find(
      (oldAdj) => oldAdj.id === newAdj.id || oldAdj.adjustmentType === newAdj.adjustmentType && oldAdj.description === newAdj.description
    )?.rate;
    newAdj.rate = formatPriceValuePercent(oldRate) || "-";
  }
  if (!pricingHasChanged) {
    newAdj.description = adj.description;
  }
  if (isConcession) {
    newAdj.description = formatDate(adj.approvalDate, "Calendar");
  }
  return newAdj;
});
const calculateVariationsTotal = (loanVariations, key = "price") => {
  return [].concat(...Object.values(loanVariations)).reduce(
    (sum, loanVariation) => sum + convertStrPercentToNumber(loanVariation[key]),
    0
  );
};
const convertStrPercentToNumber = (str) => {
  if (!str) return 0;
  if (typeof str === "number") return str;
  return Number(str.replace("%", ""));
};
const emptyAdj = {
  price: null,
  approvalDate: "-",
  description: "-"
};
const generatePricingGridRows = ({
  mappedAdjustments,
  // loanVariations = {},
  // adjustments = [],
  isValidate = false,
  rowsBeforeEdit = [],
  pricingHasChanged,
  isBuyside = false,
  isSellSide = false,
  roles = [],
  rights = {}
}) => {
  const extraPrice = calculateVariationsTotal(
    {
      Adjustment: mappedAdjustments.filter(
        // Filter out generated adjustments that have already been priced in to the FINAL adjustment
        (adj) => adj.adjustmentType === "Adjustment" && adj.priceAdjustmentType
      ),
      LockExtensionAdjustment: mappedAdjustments.filter(
        (adj) => adj.adjustmentType === "LockExtensionAdjustment"
      ),
      ReLockFeeAdjustment: mappedAdjustments.filter(
        (adj) => adj.adjustmentType === "ReLockFeeAdjustment"
      ),
      CustomPriceAdjustment: mappedAdjustments.filter(
        (adj) => adj.adjustmentType === "CustomPriceAdjustment"
      ),
      ...!isSellSide ? {
        CorporatePriceConcession: mappedAdjustments.filter(
          (adj) => adj.adjustmentType === "CorporatePriceConcession"
        ),
        BranchPriceConcession: mappedAdjustments.filter(
          (adj) => adj.adjustmentType === "BranchPriceConcession"
        )
      } : {}
    },
    "price"
  );
  const extraRate = calculateVariationsTotal(
    {
      Adjustment: mappedAdjustments.filter(
        (adj) => adj.adjustmentType === "Adjustment" && adj.priceAdjustmentType
      ),
      LockExtensionAdjustment: mappedAdjustments.filter(
        (adj) => adj.adjustmentType === "LockExtensionAdjustment"
      ),
      ReLockFeeAdjustment: mappedAdjustments.filter(
        (adj) => adj.adjustmentType === "ReLockFeeAdjustment"
      ),
      CustomRateAdjustment: mappedAdjustments.filter(
        (adj) => adj.adjustmentType === "CustomRateAdjustment"
      ),
      ...!isSellSide ? {
        CorporatePriceConcession: mappedAdjustments.filter(
          (adj) => adj.adjustmentType === "CorporatePriceConcession"
        ),
        BranchPriceConcession: mappedAdjustments.filter(
          (adj) => adj.adjustmentType === "BranchPriceConcession"
        )
      } : {}
    },
    "rate"
  );
  let parsedMappedAdjustments = mappedAdjustments.map(
    (adj) => parseAdjustment(adj)
  );
  let adjsProfitMarginRows = [];
  const viewHoldBackRole = 12;
  const viewLOCompRole = 20;
  const hasViewHoldBackrights = roles.some(
    ({ roleId }) => Number(roleId) === viewHoldBackRole
  );
  const hasLOCompViewRight = roles.some(
    ({ roleId }) => Number(roleId) === viewLOCompRole
  );
  [parsedMappedAdjustments, adjsProfitMarginRows] = partitionArray(
    parsedMappedAdjustments,
    ({ adjustmentType, priceAdjustmentType }) => adjustmentType !== "Holdback" && adjustmentType !== "Profit" && priceAdjustmentType !== "ProfitMargin"
  );
  if (!hasViewHoldBackrights && !hasLOCompViewRight) {
    parsedMappedAdjustments = parsedMappedAdjustments.filter(
      (adjustment) => adjustment.description !== "L.O. Compensation"
    );
  }
  adjsProfitMarginRows = adjsProfitMarginRows.filter(({ margin }) => !margin);
  const getFinalPrice = isSellSide ? parsedMappedAdjustments.find((el) => el.adjustmentType === "AllIn") ?? parsedMappedAdjustments.find((el) => el.adjustmentType === "Final") : parsedMappedAdjustments.find((el) => el.adjustmentType === "Final");
  const currentPrice = getFinalPrice?.currentPrice && formatPriceValue(safeSum(Number(getFinalPrice.currentPrice), extraPrice));
  const price = getFinalPrice && formatPriceValue(
    safeSum(
      Number(getFinalPrice.price),
      isValidate && pricingHasChanged ? 0 : extraPrice
    )
  );
  const currentRate = getFinalPrice?.currentRate && formatPriceValuePercent(
    safeSum(convertStrPercentToNumber(getFinalPrice.currentRate), extraRate)
  );
  const rate = getFinalPrice && formatPriceValuePercent(
    safeSum(
      convertStrPercentToNumber(getFinalPrice.rate),
      isValidate && pricingHasChanged ? 0 : extraRate
    )
  );
  const finalPrice = getFinalPrice && {
    ...getFinalPrice,
    description: "FINAL PRICE",
    price,
    rate,
    ...getFinalPrice.currentPrice ? { currentPrice } : {},
    ...getFinalPrice.currentRate ? { currentRate } : {}
  };
  const getBasePrice = parsedMappedAdjustments.find(
    (el) => el.adjustmentType === "Base"
  );
  const basePrice = getBasePrice && {
    ...getBasePrice,
    description: "Base Price"
  };
  const adjustmentTypesBlacklist = [
    "Base",
    "Final",
    ...isSellSide ? ["AllIn"] : [],
    "LockExtensionAdjustment",
    "ReLockFeeAdjustment",
    "CustomPriceAdjustment",
    "CustomRateAdjustment",
    "CorporatePriceConcession",
    "BranchPriceConcession"
  ];
  const adjustmentRows = parsedMappedAdjustments.filter(
    (el) => !adjustmentTypesBlacklist.includes(el.adjustmentType) && !el.margin
  );
  const emptyAdjArr = isBuyside ? [] : [emptyAdj];
  if (getAppConfigValue("partner-ui.showPricingDetails")) {
    return [
      ...basePrice ? [basePrice] : [],
      generateSectionTitle("Adjustments"),
      ...adjustmentRows,
      ...[
        ...rights.viewProfitMargin && !isSellSide ? [generateSectionTitle("Profitability"), ...adjsProfitMarginRows] : [],
        generateSectionTitle("Lock Extensions"),
        ...formatAdjustmentsOrConcessions({
          adjustments: parsedMappedAdjustments.filter(
            (adj) => adj.adjustmentType === "LockExtensionAdjustment"
          ) || emptyAdjArr,
          isValidate,
          rowsBeforeEdit,
          isConcession: false,
          pricingHasChanged
        }),
        generateSectionTitle(
          "Re-Lock Fees",
          rights.editRelockFee && rights.accessBuyside && !isValidate,
          "ReLockFeeAdjustment"
        ),
        ...formatAdjustmentsOrConcessions({
          adjustments: parsedMappedAdjustments.filter(
            (adj) => adj.adjustmentType === "ReLockFeeAdjustment"
          ) || emptyAdjArr,
          isValidate,
          rowsBeforeEdit,
          isConcession: false,
          pricingHasChanged
        }),
        generateSectionTitle(
          "Custom Price Adjustments",
          rights.editCustomAdjustments && rights.accessBuyside && !isValidate,
          "CustomPriceAdjustment"
        ),
        ...formatAdjustmentsOrConcessions({
          adjustments: parsedMappedAdjustments.filter(
            (adj) => adj.adjustmentType === "CustomPriceAdjustment"
          ) || emptyAdjArr,
          isValidate,
          rowsBeforeEdit,
          isConcession: false,
          pricingHasChanged
        }),
        generateSectionTitle(
          "Custom Rate Adjustments",
          rights.editCustomAdjustments && rights.accessBuyside && !isValidate,
          "CustomRateAdjustment"
        ),
        ...formatAdjustmentsOrConcessions({
          adjustments: parsedMappedAdjustments.filter(
            (adj) => adj.adjustmentType === "CustomRateAdjustment"
          ) || emptyAdjArr,
          isValidate,
          rowsBeforeEdit,
          isConcession: false,
          pricingHasChanged
        }),
        ...!isSellSide ? [
          generateSectionTitle(
            "Corporate Price Concessions",
            rights.editConcessions && rights.accessBuyside && !isValidate,
            "CorporatePriceConcession"
          ),
          ...formatAdjustmentsOrConcessions({
            adjustments: parsedMappedAdjustments.filter(
              (adj) => adj.adjustmentType === "CorporatePriceConcession"
            ) || emptyAdjArr,
            isValidate,
            rowsBeforeEdit,
            isConcession: true,
            pricingHasChanged
          }),
          generateSectionTitle(
            "Branch Price Concessions",
            rights.editConcessions && rights.accessBuyside && !isValidate,
            "BranchPriceConcession"
          ),
          ...formatAdjustmentsOrConcessions({
            adjustments: parsedMappedAdjustments.filter(
              (adj) => adj.adjustmentType === "BranchPriceConcession"
            ) || emptyAdjArr,
            isValidate,
            rowsBeforeEdit,
            isConcession: true,
            pricingHasChanged
          })
        ] : []
      ],
      ...finalPrice ? [finalPrice] : []
    ];
  }
  return [
    ...basePrice ? [basePrice] : [],
    generateSectionTitle("Adjustments"),
    ...adjustmentRows,
    ...[
      ...rights.viewProfitMargin ? [generateSectionTitle("Profitability"), ...adjsProfitMarginRows] : []
    ],
    ...finalPrice ? [finalPrice] : []
  ];
};
export {
  AdjustmentType,
  generatePricingGridRows
};
